import {
	InputGroup,
	InputGroupAddon,
	InputGroupButton,
	InputGroupInput,
} from "@client/ui/InputGroup";
import { cn } from "@client/utils/cn";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { useState } from "react";

interface InputProps extends React.ComponentProps<"input"> {
	icon?: React.ReactNode;
	label?: string;
	labelClassName?: string;
	wrapperClassName?: string;
	actionIcon?: React.ReactNode;
	actionClick?: () => void;
	labelRef?: React.RefObject<HTMLLabelElement> | null;
	inputSize?: "sm" | "lg";
}

function Input({
	className,
	type,
	icon,
	label,
	labelClassName,
	wrapperClassName,
	actionIcon,
	actionClick,
	labelRef,
	inputSize = "lg",
	onKeyDown,
	...props
}: InputProps) {
	const [passwordVisible, setPasswordVisible] = useState(false);
	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		onKeyDown?.(event);
		if (event.key === "Enter" && !event.nativeEvent.isComposing) {
			actionClick?.();
		}
	};
	const isPassword = type === "password";

	return (
		<label
			className={cn(
				"flex min-w-0 flex-1 flex-col gap-2",
				wrapperClassName,
			)}
			ref={labelRef}
		>
			{label && (
				<span
					className={cn(
						"mobile-body-2-bold text-white lg:body-2-bold",
						labelClassName,
						props.disabled && "opacity-50",
					)}
				>
					{label}
				</span>
			)}
			<InputGroup size={inputSize}>
				<InputGroupInput
					type={passwordVisible ? "text" : type}
					className={cn(className, type === "email" && "lowercase")}
					{...props}
					onKeyDown={handleKeyDown}
				/>
				{(icon || isPassword) && (
					<InputGroupAddon align="inline-end">
						{isPassword ? (
							<InputGroupButton
								onClick={() =>
									setPasswordVisible((visible) => !visible)
								}
							>
								{passwordVisible ? <EyeOffIcon /> : <EyeIcon />}
							</InputGroupButton>
						) : (
							icon
						)}
					</InputGroupAddon>
				)}
				{actionIcon && (
					<InputGroupAddon align="inline-end">
						<InputGroupButton
							variant="default"
							onMouseDown={(event) => event.preventDefault()}
							onClick={actionClick}
						>
							{actionIcon}
						</InputGroupButton>
					</InputGroupAddon>
				)}
			</InputGroup>
		</label>
	);
}

export default Input;
